import { Chip, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  buildConditionDraft,
  conditionDifferences,
  conditionQuestions,
  findAnswer,
  labelFor,
  type ConditionDraft,
} from '@/features/operations/condition';
import type {
  DeviceInspectionSummary,
  InspectionFieldSummary,
  QuotationAnswerSummary,
} from '@/features/operations/types';

/**
 * The condition half of the device's sheet: the same configured questions the applicant
 * answered (#160), answered again with the phone in hand (#89).
 *
 * It is not a step of its own. Reception and inspection are one moment at the counter, so
 * these inputs sit inside whatever form is open -- the reception form, or the sheet's edit --
 * and are saved with it in a single write.
 *
 * "Observaciones" is shown only when `onNotesChange` is passed: at reception it becomes the
 * device's first note; afterwards notes are written from the device's timeline.
 */
export function ConditionFields({
  fields,
  declared,
  draft,
  notes,
  onChange,
  onNotesChange,
}: {
  fields: InspectionFieldSummary[];
  declared: QuotationAnswerSummary[];
  draft: ConditionDraft;
  notes?: string;
  onChange: (fieldId: string, value: string) => void;
  onNotesChange?: (value: string) => void;
}) {
  return (
    <Grid container spacing={1.5}>
      {conditionQuestions(fields).map((field) => (
        <Grid item xs={12} sm={6} key={field.fieldId}>
          <TextField
            select
            fullWidth
            size="small"
            label={field.fieldLabel}
            // "Sin observar" has to read as the current state, not as a blank box: not having
            // checked something is an answer the sheet reports.
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            value={draft[field.fieldId] || ''}
            onChange={(event) => onChange(field.fieldId, event.target.value)}
          >
            <MenuItem value="">Sin observar</MenuItem>
            {/*
              Retired options stay on offer: the phone can be in the state the form stopped
              asking about, and hiding it would make this unanswerable.
            */}
            {field.options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.isActive ? option.label : `${option.label} (retirada)`}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="text.secondary">
            Declarado: {findAnswer(declared, field.fieldId)?.answerLabel ?? '—'}
          </Typography>
        </Grid>
      ))}
      {onNotesChange ? (
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Observaciones"
            helperText="Queda como nota del equipo, con tu usuario y la fecha."
            value={notes ?? ''}
            onChange={(event) => onNotesChange(event.target.value)}
          />
        </Grid>
      ) : null}
    </Grid>
  );
}

/** The condition as recorded, with what contradicts the declaration named rather than implied. */
export function ConditionSummary({
  fields,
  declared,
  inspection,
}: {
  fields: InspectionFieldSummary[];
  declared: QuotationAnswerSummary[];
  inspection: DeviceInspectionSummary | null;
}) {
  const draft = buildConditionDraft(fields, inspection);
  const differing = new Set(
    conditionDifferences(fields, declared, draft).map((row) => row.fieldId),
  );

  return (
    <Stack spacing={0.75}>
      {conditionQuestions(fields).map((field) => (
        <Stack key={field.fieldId} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2">
            {field.fieldLabel}: {labelFor(field, draft[field.fieldId] || '') ?? 'Sin observar'}
          </Typography>
          {/*
            The difference is named, not only coloured: colour alone does not survive printing
            or reach a colour-blind reader.
          */}
          {differing.has(field.fieldId) ? (
            <Chip
              size="small"
              color="warning"
              label={`Difiere de lo declarado: ${findAnswer(declared, field.fieldId)?.answerLabel ?? '—'}`}
            />
          ) : null}
        </Stack>
      ))}
    </Stack>
  );
}
